import { type StoreApi } from "zustand/vanilla";
import { type VaultState } from "../model/vaultStore.ts";
import { type VaultTreeNode } from "../model/types.ts";
import { DND_PATH_TYPE } from "../render/CanvasView.ts";
import { isHiddenFolderPath } from "../vault/attachments.ts";
import { CANVAS_SVG, DOC_SVG, FOLDER_SVG } from "./glyphs.ts";

export interface SidebarCallbacks {
  /** A file row was clicked. `peek` viene de ⌘/Ctrl+click: al costado, no en grande. */
  onOpen: (path: string, opts: { peek: boolean }) => void;
  /** A row's "⋯" menu or right-click — opens the options popup anchored at (x, y). */
  onOptions: (path: string, name: string, isFolder: boolean, x: number, y: number) => void;
  /** A file or folder was dragged and dropped onto `destDir` ("" = root). */
  onMove: (path: string, destDir: string) => void;
}

export interface SidebarHosts {
  /** Contenedor con scroll que envuelve a los tres de abajo. Sobre él se
   *  buscan las filas para marcarlas, sin importar en cuál de los tres cayeron. */
  files: HTMLElement;
  /** Grupo de atajos fijados a mano. */
  pinned: HTMLElement;
  /** Grupo de lo visitado hace poco. */
  recents: HTMLElement;
  /** El árbol del vault. */
  tree: HTMLElement;
}

/** MIME-like key for dragging a tree row to reorganize it within the sidebar
 *  (as opposed to `DND_PATH_TYPE`, used to embed a .md file onto the canvas). */
const MOVE_TYPE = "text/notespace-entry-path";

type GroupKey = "pinned" | "recents";

/** Cuántos recientes se muestran. Es un atajo, no un registro de la sesión. */
const RECENTS_SHOWN = 6;

/** Vault-relative path of the entry's parent folder ("" for root-level entries). */
function parentOf(path: string): string {
  const i = path.lastIndexOf("/");
  return i === -1 ? "" : path.slice(0, i);
}

/**
 * Renders the vault file tree and routes file/folder interactions. Subscribes
 * to the vault store so it re-renders whenever the tree or active document
 * changes. Rows are draggable so files/folders can be reorganized between
 * folders (dropped on a folder row, on another file to join its folder, or on
 * blank space to move to the root), and .md files can additionally be dropped
 * onto a canvas as `file` nodes. Folders can be expanded/collapsed,
 * editor-style; the active document's ancestor folders are always kept open
 * so it stays visible.
 */
export class Sidebar {
  /** Vault-relative paths of folders the user has expanded. */
  private expanded = new Set<string>();
  /** Whether the vault-root row (the first, always-present folder) is expanded. */
  private rootExpanded = true;
  /** Folder whose chevron should animate on the next render (the one the user
   *  just clicked). Abrir una carpeta reconstruye el árbol, así que la flecha
   *  girada es un elemento nuevo: nace ya rotada y una `transition` no llega a
   *  correr nunca. Marcamos esa fila para que el giro entre como animación de
   *  aparición, que sí se reproduce al insertarse en el DOM. `""` es la raíz. */
  private justToggled: string | null = null;
  /** Path of the entry currently being dragged, if any (drag source lives in
   *  this same sidebar, so we track it locally instead of relying on
   *  dataTransfer.getData(), which browsers restrict to the `drop` event). */
  private draggingPath: string | null = null;
  /** Last rendered state, to tell a structural change from a selection change. */
  private lastTree: VaultTreeNode[] | null = null;
  private lastAdapter: VaultState["adapter"] = null;
  private lastActive: string | null = null;
  private lastPeek: string | null = null;
  private lastPinned: string[] | null = null;
  private lastRecents: string[] | null = null;

  constructor(
    private hosts: SidebarHosts,
    private vault: StoreApi<VaultState>,
    private cb: SidebarCallbacks,
  ) {
    this.vault.subscribe(() => this.sync());
    this.renderTree();
    this.renderGroups();

    // Blank space below the rows: drop here to move an entry to the root.
    // Row-level drop handlers stop propagation, so this only fires for
    // genuine blank-area drops.
    this.hosts.tree.addEventListener("dragover", (e) => {
      if (!this.canDropOn("")) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    });
    this.hosts.tree.addEventListener("drop", (e) => {
      if (!this.canDropOn("")) return;
      e.preventDefault();
      this.cb.onMove(this.draggingPath!, "");
    });
  }

  /** Can the entry currently being dragged be dropped onto `destDir`? */
  private canDropOn(destDir: string): boolean {
    const src = this.draggingPath;
    if (!src) return false;
    if (src === destDir) return false;
    if (destDir.startsWith(`${src}/`)) return false; // no dropping a folder into its own descendant
    if (parentOf(src) === destDir) return false; // already there
    return true;
  }

  /**
   * Store changed: rehacer lo MENOS posible.
   *
   * El rail tiene tres partes que cambian por motivos distintos, y reconstruir
   * las tres ante cualquier cambio era lo que hacía parpadear la columna entera
   * en cada cambio de archivo. Abrir un documento reordena los recientes: si
   * eso arrastrara al árbol, el parpadeo volvería por la puerta de al lado.
   *
   * Los grupos SÍ se rehacen cuando cambia el árbol, porque se filtran contra
   * lo que existe: un archivo borrado tiene que irse también de sus atajos.
   */
  private sync(): void {
    const { tree, adapter, activePath, peekPath, pinned, recents } = this.vault.getState();

    if (adapter !== this.lastAdapter || tree !== this.lastTree) {
      this.renderTree();
      this.lastAdapter = adapter;
      this.lastTree = tree;
      this.lastPinned = pinned;
      this.lastRecents = recents;
      this.renderGroups();
    } else {
      if (pinned !== this.lastPinned || recents !== this.lastRecents || activePath !== this.lastActive) {
        this.lastPinned = pinned;
        this.lastRecents = recents;
        this.renderGroups();
      }
      // Una fila escondida dentro de una carpeta cerrada no se puede marcar:
      // hay que abrir la carpeta, y eso es rehacer el árbol.
      if (activePath !== this.lastActive && !this.ancestorsExpanded(activePath)) {
        this.renderTree();
      }
    }

    this.lastActive = activePath;
    this.lastPeek = peekPath;
    this.mark(activePath, peekPath);
  }

  /**
   * Pinta qué está abierto y dónde.
   *
   * Una misma ruta puede estar en pantalla hasta tres veces —fijada, reciente y
   * en el árbol— así que se marcan TODAS sus filas: media marca es peor que
   * ninguna. Se buscan en el DOM en vez de llevar un índice, porque el índice
   * habría que rehacerlo cada vez que se reconstruye cualquiera de los tres
   * pedazos, y esto son unas decenas de nodos.
   */
  private mark(active: string | null, peek: string | null): void {
    for (const el of this.hosts.files.querySelectorAll<HTMLElement>(".tree-row.active, .tree-row.peeking")) {
      el.classList.remove("active", "peeking");
    }
    for (const el of this.rowsFor(active)) el.classList.add("active");
    for (const el of this.rowsFor(peek)) el.classList.add("peeking");
  }

  private rowsFor(path: string | null): HTMLElement[] {
    if (!path) return [];
    const sel = `.tree-row[data-path="${CSS.escape(path)}"]`;
    return [...this.hosts.files.querySelectorAll<HTMLElement>(sel)];
  }

  /** Is every ancestor folder of `path` already expanded (i.e. its row exists)? */
  private ancestorsExpanded(path: string | null): boolean {
    if (!path) return true;
    const parts = path.split("/");
    parts.pop();
    let acc = "";
    for (const part of parts) {
      acc = acc ? `${acc}/${part}` : part;
      if (!this.expanded.has(acc)) return false;
    }
    return true;
  }

  private renderTree(): void {
    const { tree, activePath, adapter } = this.vault.getState();
    // Only auto-reveal when the active document actually changed (e.g. a new
    // file was opened) — not on every render, or a manual collapse would get
    // silently undone by the very re-render the toggle triggers (and by any
    // unrelated tree refresh, e.g. creating a file elsewhere).
    if (activePath && activePath !== this.lastActive) this.revealAncestors(activePath);
    this.hosts.tree.replaceChildren();
    if (!adapter) return;

    this.hosts.tree.appendChild(this.rootRow(this.vault.getState().name, this.rootExpanded));
    if (!this.rootExpanded) return;

    const childWrap = document.createElement("div");
    childWrap.className = "tree-children";
    if (tree.length === 0) {
      const empty = document.createElement("div");
      empty.className = "tree-row";
      empty.style.color = "var(--color-text-muted)";
      empty.style.cursor = "default";
      empty.textContent = "Empty — create a note or a canvas.";
      childWrap.appendChild(empty);
    } else {
      childWrap.appendChild(this.renderNodes(tree));
    }
    this.hosts.tree.appendChild(childWrap);
  }

  // ---- atajos: fijados y recientes -----------------------------------------

  /** Rutas de archivo que existen HOY en el vault. */
  private existingFiles(): Set<string> {
    const out = new Set<string>();
    const walk = (nodes: VaultTreeNode[]): void => {
      for (const n of nodes) {
        if (n.children) {
          if (!isHiddenFolderPath(n.path)) walk(n.children);
        } else out.add(n.path);
      }
    };
    walk(this.vault.getState().tree);
    return out;
  }

  private renderGroups(): void {
    const { pinned, recents, activePath } = this.vault.getState();
    const alive = this.existingFiles();
    this.renderGroup(this.hosts.pinned, "pinned", "Pinned", pinned.filter((p) => alive.has(p)));
    // Lo que está abierto en grande no es "reciente": ya se está mirando, y
    // ocuparía el primer renglón con el único documento que no hace falta ir a
    // buscar. Recientes es dónde estuviste, no dónde estás.
    this.renderGroup(
      this.hosts.recents,
      "recents",
      "Recent",
      recents.filter((p) => alive.has(p) && p !== activePath).slice(0, RECENTS_SHOWN),
    );
  }

  /** Un grupo vacío no deja encabezado: quien no fija nada no ve la sección. */
  private renderGroup(host: HTMLElement, key: GroupKey, title: string, paths: string[]): void {
    host.replaceChildren();
    host.hidden = paths.length === 0;
    if (paths.length === 0) return;
    const open = this.groupOpen(key);
    host.appendChild(this.groupHead(key, title, open));
    if (!open) return;
    const wrap = document.createElement("div");
    wrap.className = "rail-group-rows";
    for (const path of paths) wrap.appendChild(this.shortcutRow(path));
    host.appendChild(wrap);
  }

  private groupHead(key: GroupKey, title: string, open: boolean): HTMLElement {
    const row = document.createElement("div");
    row.className = "tree-row rail-group-head";
    row.append(
      chevronIcon(open, this.justToggled === key),
      svgIcon(key === "pinned" ? STAR_SVG : RETURN_SVG),
      label(title),
    );
    row.addEventListener("click", () => {
      this.setGroupOpen(key, !open);
      this.justToggled = key;
      this.renderGroups();
      this.justToggled = null;
      this.mark(this.lastActive, this.lastPeek);
    });
    return row;
  }

  /**
   * Fila de un atajo.
   *
   * Es la misma fila del árbol —mismo alto, mismo ícono, mismo click— menos lo
   * que es propio del árbol: no se arrastra para reorganizar ni recibe nada
   * encima, porque su lugar en la lista no dice dónde vive el archivo. Sí se
   * puede arrastrar al lienzo, que es la mitad de la gracia de tenerlo a mano.
   */
  private shortcutRow(path: string): HTMLElement {
    const name = path.split("/").pop() ?? path;
    const isCanvas = path.toLowerCase().endsWith(".canvas");
    const row = document.createElement("div");
    row.className = "tree-row";
    row.dataset.path = path;
    const menu = menuButton();
    row.append(
      chevronSpacer(),
      svgIcon(isCanvas ? CANVAS_SVG : DOC_SVG),
      label(name),
      menu,
    );
    row.title = path;
    row.addEventListener("click", (e) =>
      this.cb.onOpen(path, { peek: e.metaKey || e.ctrlKey }),
    );
    row.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.cb.onOptions(path, name, false, e.clientX, e.clientY);
    });
    menu.addEventListener("click", (e) => {
      e.stopPropagation();
      const r = menu.getBoundingClientRect();
      this.cb.onOptions(path, name, false, r.left, r.bottom + 4);
    });
    if (!isCanvas) {
      row.draggable = true;
      row.addEventListener("dragstart", (e) => {
        e.dataTransfer?.setData(DND_PATH_TYPE, path);
        if (e.dataTransfer) e.dataTransfer.effectAllowed = "copy";
      });
    }
    return row;
  }

  /** Si un grupo está desplegado. Se recuerda entre sesiones. */
  private groupOpen(key: GroupKey): boolean {
    try {
      return localStorage.getItem(`blanqo.rail.${key}`) !== "closed";
    } catch {
      return true;
    }
  }

  private setGroupOpen(key: GroupKey, open: boolean): void {
    try {
      localStorage.setItem(`blanqo.rail.${key}`, open ? "open" : "closed");
    } catch {
      // Modo privado / almacenamiento lleno: vale para esta sesión.
    }
  }

  /** The vault root itself, rendered as the first (always-present) folder row.
   *  Toggling it collapses the whole tree; it's also a drop target for moving
   *  entries back to the root. */
  private rootRow(name: string, expanded: boolean): HTMLElement {
    const row = document.createElement("div");
    row.className = "tree-row tree-row-folder tree-row-root";
    const menu = menuButton();
    row.append(
      chevronIcon(expanded, this.justToggled === ""),
      svgIcon(FOLDER_SVG),
      label(name || "Vault"),
      menu,
    );
    row.title = name;
    const toggle = () => {
      this.rootExpanded = !this.rootExpanded;
      this.justToggled = "";
      this.renderTree();
      this.justToggled = null;
      this.mark(this.lastActive, this.lastPeek);
    };
    row.addEventListener("click", toggle);
    row.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.cb.onOptions("", name, true, e.clientX, e.clientY);
    });
    menu.addEventListener("click", (e) => {
      e.stopPropagation();
      const r = menu.getBoundingClientRect();
      this.cb.onOptions("", name, true, r.left, r.bottom + 4);
    });
    this.makeDropTarget(row, "");
    return row;
  }

  /** Add every ancestor folder of `path` to the expanded set. */
  private revealAncestors(path: string): void {
    const parts = path.split("/");
    parts.pop(); // drop the leaf (file) name
    let acc = "";
    for (const part of parts) {
      acc = acc ? `${acc}/${part}` : part;
      this.expanded.add(acc);
    }
  }

  private toggle(path: string): void {
    if (this.expanded.has(path)) this.expanded.delete(path);
    else this.expanded.add(path);
    this.justToggled = path;
    this.renderTree();
    this.justToggled = null;
    this.mark(this.lastActive, this.lastPeek);
  }

  private renderNodes(nodes: VaultTreeNode[]): DocumentFragment {
    const frag = document.createDocumentFragment();
    for (const node of nodes) {
      // Pasted-image store: a real on-disk folder, hidden from Blanq's UI.
      if (node.children && isHiddenFolderPath(node.path)) continue;
      if (node.children) {
        const expanded = this.expanded.has(node.path);
        frag.appendChild(this.folderRow(node, expanded));
        if (expanded) {
          const childWrap = document.createElement("div");
          childWrap.className = "tree-children";
          childWrap.appendChild(this.renderNodes(node.children));
          frag.appendChild(childWrap);
        }
      } else {
        frag.appendChild(this.fileRow(node));
      }
    }
    return frag;
  }

  private folderRow(node: VaultTreeNode, expanded: boolean): HTMLElement {
    const row = document.createElement("div");
    row.className = "tree-row tree-row-folder";
    const menu = menuButton();
    menu.title = "New, rename or delete";
    row.append(
      chevronIcon(expanded, this.justToggled === node.path),
      svgIcon(FOLDER_SVG),
      label(node.name),
      menu,
    );
    row.title = node.path;
    row.addEventListener("click", () => this.toggle(node.path));
    row.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.cb.onOptions(node.path, node.name, true, e.clientX, e.clientY);
    });
    menu.addEventListener("click", (e) => {
      e.stopPropagation();
      const r = menu.getBoundingClientRect();
      this.cb.onOptions(node.path, node.name, true, r.left, r.bottom + 4);
    });
    this.makeDraggable(row, node.path);
    this.makeDropTarget(row, node.path);
    return row;
  }

  private fileRow(node: VaultTreeNode): HTMLElement {
    const row = document.createElement("div");
    row.className = "tree-row";
    row.dataset.path = node.path;
    const menu = menuButton();
    row.append(
      chevronSpacer(),
      svgIcon(node.kind === "canvas" ? CANVAS_SVG : DOC_SVG),
      label(node.name),
      menu,
    );
    row.title = node.path;
    row.addEventListener("click", (e) =>
      this.cb.onOpen(node.path, { peek: e.metaKey || e.ctrlKey }),
    );
    row.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.cb.onOptions(node.path, node.name, false, e.clientX, e.clientY);
    });
    menu.addEventListener("click", (e) => {
      e.stopPropagation();
      const r = menu.getBoundingClientRect();
      this.cb.onOptions(node.path, node.name, false, r.left, r.bottom + 4);
    });
    // Un PDF se embebe igual que una nota: la tarjeta muestra su portada.
    this.makeDraggable(row, node.path, node.kind === "md" || node.kind === "pdf");
    // Dropping onto a file moves the dragged entry alongside it (into its parent folder).
    this.makeDropTarget(row, parentOf(node.path));
    return row;
  }

  /** Wire `row` as a drag source for `path`. Embeddable files (`.md`, `.pdf`)
   *  also advertise `DND_PATH_TYPE` so they can be dropped onto the canvas as
   *  `file` nodes. */
  private makeDraggable(row: HTMLElement, path: string, embeddable = false): void {
    row.draggable = true;
    row.addEventListener("dragstart", (e) => {
      this.draggingPath = path;
      e.dataTransfer?.setData(MOVE_TYPE, path);
      if (embeddable) e.dataTransfer?.setData(DND_PATH_TYPE, path);
      if (e.dataTransfer) e.dataTransfer.effectAllowed = "all";
    });
    row.addEventListener("dragend", () => {
      this.draggingPath = null;
      row.classList.remove("drop-target");
    });
  }

  /** Wire `row` as a drop target that moves the dragged entry into `destDir`. */
  private makeDropTarget(row: HTMLElement, destDir: string): void {
    row.addEventListener("dragover", (e) => {
      if (!this.canDropOn(destDir)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
      row.classList.add("drop-target");
    });
    row.addEventListener("dragleave", () => row.classList.remove("drop-target"));
    row.addEventListener("drop", (e) => {
      row.classList.remove("drop-target");
      if (!this.canDropOn(destDir)) return;
      e.preventDefault();
      e.stopPropagation();
      this.cb.onMove(this.draggingPath!, destDir);
    });
  }
}

// La carpeta (`FOLDER_SVG`) vive en `glyphs.ts` con el resto de los glifos
// compartidos: acá se usa la variante `currentColor`, que sigue el color de la
// fila porque es estructura, no identidad. La flecha, en cambio, es sólo del
// árbol.
/**
 * Marcas de los dos grupos de atajos.
 *
 * Van en `currentColor` y no en el azul de sistema, como la carpeta del árbol:
 * acá la marca es estructura —dice qué clase de lista es— y sigue el gris del
 * encabezado. El azul queda para los glifos que dicen QUÉ es un archivo.
 *
 * Viven acá y no en `glyphs.ts` por el mismo motivo que la flecha del árbol:
 * son sólo del rail, no glifos compartidos entre el árbol, el buscador y el
 * menú de crear.
 */
const STAR_SVG =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3.9 2.6 5.2 5.8.85-4.2 4.1 1 5.75L12 17.1l-5.2 2.7 1-5.75-4.2-4.1 5.8-.85Z"/></svg>';

/**
 * La flecha que vuelve, para los recientes.
 *
 * No es un reloj a propósito: el grupo no habla de la hora sino de por dónde se
 * pasó, y el mismo gesto que lo llena —volver sobre lo andado— es el de las
 * flechas de atrás y adelante de la fila de marca. Que compartan forma es lo
 * que ata el atajo con el rastro.
 *
 * El arco abre arriba a la izquierda y la punta se arma con la esquina: es la
 * misma construcción que el resto de los glifos —trazo de 1.7, uniones
 * redondeadas— dentro de la caja óptica de la estrella.
 */
const RETURN_SVG =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3.9 12a8.1 8.1 0 1 0 8.1-8.1 8.8 8.8 0 0 0-6.07 2.47L3.9 8.4"/><path d="M3.9 3.9v4.5h4.5"/></svg>';

const CHEVRON_SVG =
  '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="m9 5.5 6.5 6.5L9 18.5"/></svg>';

/** `animate`: la fila acaba de togglearse, así que el giro entra como
 *  animación de aparición (ver `justToggled`). Las demás flechas se pintan
 *  directamente en su ángulo final, sin movimiento. */
function chevronIcon(expanded: boolean, animate = false): HTMLElement {
  const el = document.createElement("span");
  el.className =
    "tw-chevron" +
    (expanded ? " expanded" : "") +
    (animate ? (expanded ? " spin-open" : " spin-close") : "");
  el.innerHTML = CHEVRON_SVG;
  return el;
}

/** Invisible placeholder in file rows so labels align with folder rows. */
function chevronSpacer(): HTMLElement {
  const el = document.createElement("span");
  el.className = "tw-chevron";
  return el;
}

function svgIcon(svg: string): HTMLElement {
  const el = document.createElement("span");
  el.className = "tw-icon";
  el.innerHTML = svg;
  return el;
}

function label(text: string): HTMLElement {
  const el = document.createElement("span");
  el.className = "tw-label";
  el.textContent = text;
  return el;
}

const DOTS_SVG =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>';

function menuButton(): HTMLButtonElement {
  const el = document.createElement("button");
  el.className = "row-menu";
  el.type = "button";
  el.title = "More options";
  el.setAttribute("aria-label", "More options");
  el.setAttribute("aria-haspopup", "menu");
  el.innerHTML = DOTS_SVG;
  return el;
}
